import React from 'react';
import { ChevronDown, Type, HelpCircle, Lightbulb } from 'lucide-react';

interface WizardStepSelectProps {
  label: string;
  value: string;
  options?: string[];
  onChange: (value: string) => void;
  error?: string;
  helperText?: string;
  required?: boolean;
  type?: 'select' | 'text';
  placeholder?: string;
  help?: string;
  example?: string;
}

export const WizardStepSelect: React.FC<WizardStepSelectProps> = ({
  label,
  value,
  options,
  onChange,
  error,
  helperText,
  required = false,
  type = 'select',
  placeholder,
  help,
  example
}) => {
  const inputId = `input-${label.toLowerCase().replace(/\s+/g, '-')}`;
  const errorId = error ? `${inputId}-error` : undefined;
  const helperId = helperText ? `${inputId}-helper` : undefined;

  return (
    <div className="p-8">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            {label}
            {required && <span className="text-red-500 ml-1">*</span>}
          </h2>
          {helperText && (
            <p
              id={helperId}
              className="text-gray-600"
            >
              {helperText}
            </p>
          )}

          {/* Help text */}
          {help && (
            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-start space-x-2">
                <HelpCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-blue-900 text-left">{help}</p>
              </div>
            </div>
          )}

          {/* Example */}
          {example && (
            <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-start space-x-2">
                <Lightbulb className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-green-900 text-left italic">{example}</p>
              </div>
            </div>
          )}
        </div>

        <div className="space-y-4">
          {type === 'text' ? (
            <div className="relative">
              <Type className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
              <input
                id={inputId}
                type="text"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                className={`
                  w-full pl-12 pr-4 py-4 text-lg border-2 rounded-xl bg-white
                  focus:ring-2 focus:ring-blue-500 focus:border-blue-500
                  transition-colors
                  ${error 
                    ? 'border-red-300 focus:border-red-500 focus:ring-red-500' 
                    : 'border-gray-300 hover:border-gray-400'
                  }
                  ${!value ? 'text-gray-500' : 'text-gray-900'}
                `}
                aria-invalid={!!error}
                aria-describedby={[errorId, helperId].filter(Boolean).join(' ') || undefined}
                required={required}
              />
            </div>
          ) : (
            <div className="relative">
            <select
              id={inputId}
              value={value}
              onChange={(e) => onChange(e.target.value)}
              className={`
                w-full px-4 py-4 text-lg border-2 rounded-xl bg-white
                focus:ring-2 focus:ring-blue-500 focus:border-blue-500
                transition-colors appearance-none cursor-pointer
                ${error 
                  ? 'border-red-300 focus:border-red-500 focus:ring-red-500' 
                  : 'border-gray-300 hover:border-gray-400'
                }
                ${!value ? 'text-gray-500' : 'text-gray-900'}
              `}
              aria-invalid={!!error}
              aria-describedby={[errorId, helperId].filter(Boolean).join(' ') || undefined}
              required={required}
            >
              <option value="" disabled>
                Sélectionnez une option...
              </option>
              {options?.map((option, index) => (
                <option key={index} value={option} className="text-gray-900">
                  {option}
                </option>
              ))}
            </select>
            
            <ChevronDown className="absolute right-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
          </div>
          )}

          {error && (
            <div 
              id={errorId}
              className="flex items-center space-x-2 text-red-600"
              role="alert"
              aria-live="polite"
            >
              <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <span className="text-sm">{error}</span>
            </div>
          )}
        </div>

        {/* Visual feedback for selection */}
        {value && (
          <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center space-x-2">
              <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              <span className="text-blue-800 font-medium">
                {type === 'text' ? 'Nom du projet:' : 'Sélection:'} {value}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};